import { DEFAULT_USER_PROFILE, SEGMENT_MODES } from "./workoutPlanUtils";
import {
  DEFAULT_REST_STYLE,
  DEFAULT_SESSION_PACE,
  MAX_TRAINING_PREFERENCES_LENGTH,
  REST_STYLES,
  SESSION_PACES,
} from "../config/trainingPreferences";

export const PROFILE_FIELD_MAP = [
  {
    localKey: "fitnessLevel",
    apiKey: "fitness_level",
    defaultValue: "intermediate",
  },
  {
    localKey: "primaryGoal",
    apiKey: "primary_goal",
    defaultValue: "general",
  },
  {
    localKey: "targetMuscles",
    apiKey: "target_muscles",
    defaultValue: ["Chest", "Back", "Core", "Legs"],
  },
  {
    localKey: "avoidedAreas",
    apiKey: "avoided_areas",
    defaultValue: [],
  },
  {
    localKey: "availableEquipment",
    apiKey: "available_equipment",
    defaultValue: ["dumbbells", "resistance bands", "pull-up bar"],
  },
  {
    localKey: "timeAvailable",
    apiKey: "time_available",
    defaultValue: 60,
  },
  {
    localKey: "workoutStyle",
    apiKey: "workout_style",
    defaultValue: "mixed",
  },
  {
    localKey: "warmupMode",
    apiKey: "warmup_mode",
    defaultValue: "auto",
  },
  {
    localKey: "cooldownMode",
    apiKey: "cooldown_mode",
    defaultValue: "off",
  },
  {
    localKey: "includeWarmup",
    apiKey: "include_warmup",
    defaultValue: true,
  },
  {
    localKey: "warmupDuration",
    apiKey: "warmup_duration",
    defaultValue: 5,
  },
  {
    localKey: "includeCooldown",
    apiKey: "include_cooldown",
    defaultValue: false,
  },
  {
    localKey: "cooldownDuration",
    apiKey: "cooldown_duration",
    defaultValue: 5,
  },
  {
    localKey: "autoWarmupThreshold",
    apiKey: "auto_warmup_threshold",
    defaultValue: 30,
  },
  {
    localKey: "autoCooldownThreshold",
    apiKey: "auto_cooldown_threshold",
    defaultValue: 30,
  },
  {
    localKey: "warmupPreferredDuration",
    apiKey: "warmup_preferred_duration",
    defaultValue: 0,
  },
  {
    localKey: "cooldownPreferredDuration",
    apiKey: "cooldown_preferred_duration",
    defaultValue: 0,
  },
  {
    localKey: "birthYear",
    apiKey: "age",
    aliases: ["birth_year"],
    defaultValue: null,
  },
  {
    localKey: "trainingExperience",
    apiKey: "training_experience",
    defaultValue: "beginner",
  },
  {
    localKey: "injuryNotes",
    apiKey: "injury_notes",
    defaultValue: "",
  },
  {
    localKey: "restStyle",
    apiKey: "rest_style",
    defaultValue: DEFAULT_REST_STYLE,
  },
  {
    localKey: "sessionPace",
    apiKey: "session_pace",
    defaultValue: DEFAULT_SESSION_PACE,
  },
  {
    localKey: "trainingPreferences",
    apiKey: "training_preferences",
    defaultValue: "",
  },
  {
    localKey: "excludedSessionShapes",
    apiKey: "excluded_session_shapes",
    defaultValue: [],
  },
  {
    localKey: "weeklySessions",
    apiKey: "weekly_sessions",
    defaultValue: null,
  },
  {
    localKey: "maxHeartRate",
    apiKey: "max_heart_rate",
    defaultValue: null,
  },
  {
    localKey: "restingHeartRate",
    apiKey: "resting_heart_rate",
    defaultValue: null,
  },
  {
    localKey: "bodyWeightKg",
    apiKey: "body_weight_kg",
    defaultValue: null,
  },
  {
    localKey: "weightUnit",
    apiKey: "weight_unit",
    defaultValue: null,
  },
  {
    localKey: "hrZoneMethod",
    apiKey: "hr_zone_method",
    defaultValue: null,
  },
  {
    localKey: "weeklyTargetOverrides",
    apiKey: "weekly_target_overrides",
    defaultValue: {},
  },
];

const isDefined = (value) => value !== undefined && value !== null;

const normaliseStringArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === "string" ? entry.trim() : entry))
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return undefined;
};

const normaliseBoolean = (value) =>
  typeof value === "boolean" ? value : undefined;

// Days a week the user aims to train: 1-14, or 0 to clear it (the server
// stores NULL and the app goes back to working it out).
const normaliseWeeklySessions = (value) => {
  const n = normaliseNumber(value);
  return n !== undefined && Number.isInteger(n) && n >= 0 && n <= 14
    ? n
    : undefined;
};

// Heart rate in bpm (#94). 0 clears it back to the estimate; otherwise a
// plausible human range for that measure.
const HEART_RATE_RANGE = { max: [100, 230], resting: [30, 110] };
const normaliseHeartRate = (value, kind) => {
  const n = normaliseNumber(value);
  if (n === undefined || !Number.isInteger(n)) return undefined;
  if (n === 0) return 0;
  const [lo, hi] = HEART_RATE_RANGE[kind];
  return n >= lo && n <= hi ? n : undefined;
};

// Body weight in kg (#194), to 0.1. 0 clears it; otherwise 25 to 300 kg.
const normaliseBodyWeight = (value) => {
  const n = normaliseNumber(value);
  if (n === undefined) return undefined;
  if (n === 0) return 0;
  return n >= 25 && n <= 300 ? Math.round(n * 10) / 10 : undefined;
};

// The unit weights show in (#191).
const normaliseWeightUnit = (value) => (value === "kg" || value === "lb" ? value : undefined);

// Raised weekly targets: { resistance|anaerobic|aerobic: 0-7 }.
const normaliseTargetOverrides = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const out = {};
  for (const tag of ["resistance", "anaerobic", "aerobic"]) {
    const n = Number(value[tag]);
    if (Number.isInteger(n) && n >= 0 && n <= 7) out[tag] = n;
  }
  return out;
};

const normaliseNumber = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// Off / Auto / On (issue #70). Anything unrecognised is dropped so a stale
// client can't write a mode the prompt builder doesn't understand.
const normaliseSegmentMode = (value) =>
  SEGMENT_MODES.includes(value) ? value : undefined;

// Same rule for the training-preference enums: drop what the API would reject.
const normaliseRestStyle = (value) =>
  REST_STYLES.includes(value) ? value : undefined;
const normaliseSessionPace = (value) =>
  SESSION_PACES.includes(value) ? value : undefined;

const normaliseFreeText = (value) =>
  typeof value === "string"
    ? value.slice(0, MAX_TRAINING_PREFERENCES_LENGTH)
    : undefined;

const hasMeaningfulValue = (value, defaultValue) => {
  if (!isDefined(value)) return false;

  if (Array.isArray(defaultValue)) {
    return (
      Array.isArray(value) &&
      JSON.stringify(value) !== JSON.stringify(defaultValue)
    );
  }

  if (typeof defaultValue === "string") {
    return value !== "" && value !== defaultValue;
  }

  if (typeof defaultValue === "boolean") {
    return typeof value === "boolean" && value !== defaultValue;
  }

  if (typeof defaultValue === "number") {
    return typeof value === "number" && value !== defaultValue;
  }

  return value !== "";
};

export function normaliseApiProfile(profile = {}) {
  return {
    fitness_level: profile.fitness_level ?? profile.fitnessLevel,
    primary_goal: profile.primary_goal ?? profile.primaryGoal,
    target_muscles: normaliseStringArray(
      profile.target_muscles ?? profile.targetMuscles,
    ),
    avoided_areas: normaliseStringArray(
      profile.avoided_areas ?? profile.avoidedAreas,
    ),
    available_equipment: normaliseStringArray(
      profile.available_equipment ?? profile.availableEquipment,
    ),
    time_available: normaliseNumber(
      profile.time_available ?? profile.timeAvailable,
    ),
    workout_style: profile.workout_style ?? profile.workoutStyle,
    warmup_mode: normaliseSegmentMode(
      profile.warmup_mode ?? profile.warmupMode,
    ),
    cooldown_mode: normaliseSegmentMode(
      profile.cooldown_mode ?? profile.cooldownMode,
    ),
    include_warmup: normaliseBoolean(
      profile.include_warmup ?? profile.includeWarmup,
    ),
    warmup_duration: normaliseNumber(
      profile.warmup_duration ?? profile.warmupDuration,
    ),
    include_cooldown: normaliseBoolean(
      profile.include_cooldown ?? profile.includeCooldown,
    ),
    cooldown_duration: normaliseNumber(
      profile.cooldown_duration ?? profile.cooldownDuration,
    ),
    auto_warmup_threshold: normaliseNumber(
      profile.auto_warmup_threshold ?? profile.autoWarmupThreshold,
    ),
    auto_cooldown_threshold: normaliseNumber(
      profile.auto_cooldown_threshold ?? profile.autoCooldownThreshold,
    ),
    warmup_preferred_duration: normaliseNumber(
      profile.warmup_preferred_duration ?? profile.warmupPreferredDuration,
    ),
    cooldown_preferred_duration: normaliseNumber(
      profile.cooldown_preferred_duration ?? profile.cooldownPreferredDuration,
    ),
    age: normaliseNumber(
      profile.age ?? profile.birth_year ?? profile.birthYear,
    ),
    training_experience:
      profile.training_experience ?? profile.trainingExperience,
    injury_notes: profile.injury_notes ?? profile.injuryNotes,
    rest_style: normaliseRestStyle(profile.rest_style ?? profile.restStyle),
    session_pace: normaliseSessionPace(
      profile.session_pace ?? profile.sessionPace,
    ),
    training_preferences: normaliseFreeText(
      profile.training_preferences ?? profile.trainingPreferences,
    ),
    excluded_session_shapes: normaliseStringArray(
      profile.excluded_session_shapes ?? profile.excludedSessionShapes,
    ),
    weekly_sessions: normaliseWeeklySessions(
      profile.weekly_sessions ?? profile.weeklySessions,
    ),
    weekly_target_overrides: normaliseTargetOverrides(
      profile.weekly_target_overrides ?? profile.weeklyTargetOverrides,
    ),
    max_heart_rate: normaliseHeartRate(profile.max_heart_rate ?? profile.maxHeartRate, "max"),
    body_weight_kg: normaliseBodyWeight(profile.body_weight_kg ?? profile.bodyWeightKg),
    weight_unit: normaliseWeightUnit(profile.weight_unit ?? profile.weightUnit),
    hr_zone_method: ["reserve", "max"].includes(profile.hr_zone_method ?? profile.hrZoneMethod)
      ? profile.hr_zone_method ?? profile.hrZoneMethod
      : undefined,
    resting_heart_rate: normaliseHeartRate(
      profile.resting_heart_rate ?? profile.restingHeartRate,
      "resting",
    ),
  };
}

export function toApiProfile(profile = {}) {
  const source = {
    ...DEFAULT_USER_PROFILE,
    ...profile,
  };

  const normalised = normaliseApiProfile(source);

  return {
    ...normalised,
    injury_notes: normalised.injury_notes ?? "",
    training_preferences: normalised.training_preferences ?? "",
  };
}

export function fromApiProfile(profile = {}) {
  const normalised = normaliseApiProfile(profile);

  // A profile saved before #70 has no mode column, so fall back to the legacy
  // include_* flag: an explicit `false` was the only way to say "no warm-up".
  const legacyMode = (include) => (include === false ? "off" : "auto");

  return {
    ...DEFAULT_USER_PROFILE,
    warmupMode:
      normalised.warmup_mode ?? legacyMode(normalised.include_warmup),
    cooldownMode:
      normalised.cooldown_mode ?? legacyMode(normalised.include_cooldown),
    fitnessLevel: normalised.fitness_level ?? DEFAULT_USER_PROFILE.fitnessLevel,
    primaryGoal: normalised.primary_goal ?? DEFAULT_USER_PROFILE.primaryGoal,
    targetMuscles:
      normalised.target_muscles ?? DEFAULT_USER_PROFILE.targetMuscles,
    avoidedAreas: normalised.avoided_areas ?? DEFAULT_USER_PROFILE.avoidedAreas,
    availableEquipment: Array.isArray(normalised.available_equipment)
      ? normalised.available_equipment.join(", ")
      : (normalised.available_equipment ??
        DEFAULT_USER_PROFILE.availableEquipment),
    timeAvailable:
      normalised.time_available ?? DEFAULT_USER_PROFILE.timeAvailable,
    workoutStyle: normalised.workout_style ?? DEFAULT_USER_PROFILE.workoutStyle,
    includeWarmup:
      normalised.include_warmup ?? DEFAULT_USER_PROFILE.includeWarmup,
    warmupDuration:
      normalised.warmup_duration ?? DEFAULT_USER_PROFILE.warmupDuration,
    includeCooldown:
      normalised.include_cooldown ?? DEFAULT_USER_PROFILE.includeCooldown,
    cooldownDuration:
      normalised.cooldown_duration ?? DEFAULT_USER_PROFILE.cooldownDuration,
    autoWarmupThreshold:
      normalised.auto_warmup_threshold ??
      DEFAULT_USER_PROFILE.autoWarmupThreshold,
    autoCooldownThreshold:
      normalised.auto_cooldown_threshold ??
      DEFAULT_USER_PROFILE.autoCooldownThreshold,
    warmupPreferredDuration:
      normalised.warmup_preferred_duration ??
      DEFAULT_USER_PROFILE.warmupPreferredDuration,
    cooldownPreferredDuration:
      normalised.cooldown_preferred_duration ??
      DEFAULT_USER_PROFILE.cooldownPreferredDuration,
    birthYear: normalised.age ?? DEFAULT_USER_PROFILE.birthYear,
    trainingExperience:
      normalised.training_experience ?? DEFAULT_USER_PROFILE.trainingExperience,
    injuryNotes: normalised.injury_notes ?? DEFAULT_USER_PROFILE.injuryNotes,
    restStyle: normalised.rest_style ?? DEFAULT_USER_PROFILE.restStyle,
    sessionPace: normalised.session_pace ?? DEFAULT_USER_PROFILE.sessionPace,
    trainingPreferences:
      normalised.training_preferences ??
      DEFAULT_USER_PROFILE.trainingPreferences,
    excludedSessionShapes:
      normalised.excluded_session_shapes ??
      DEFAULT_USER_PROFILE.excludedSessionShapes,
    weeklySessions:
      normalised.weekly_sessions ?? DEFAULT_USER_PROFILE.weeklySessions,
    weeklyTargetOverrides:
      normalised.weekly_target_overrides ??
      DEFAULT_USER_PROFILE.weeklyTargetOverrides,
    // 0 and null both mean "use the estimate".
    maxHeartRate: normalised.max_heart_rate || DEFAULT_USER_PROFILE.maxHeartRate,
    restingHeartRate: normalised.resting_heart_rate || DEFAULT_USER_PROFILE.restingHeartRate,
    bodyWeightKg: normalised.body_weight_kg || DEFAULT_USER_PROFILE.bodyWeightKg,
    weightUnit: normalised.weight_unit || DEFAULT_USER_PROFILE.weightUnit,
    hrZoneMethod: normalised.hr_zone_method || DEFAULT_USER_PROFILE.hrZoneMethod,
  };
}

export function mergeLocalProfileWithCloud(
  localProfile = {},
  cloudProfile = {},
) {
  const localApi = toApiProfile(localProfile);
  const cloudApi = normaliseApiProfile(cloudProfile);
  const mergedApi = {};

  for (const field of PROFILE_FIELD_MAP) {
    const cloudValue = cloudApi[field.apiKey];
    const localValue = localApi[field.apiKey];

    if (hasMeaningfulValue(cloudValue, field.defaultValue)) {
      mergedApi[field.apiKey] = cloudValue;
      continue;
    }

    if (isDefined(localValue)) {
      mergedApi[field.apiKey] = localValue;
      continue;
    }

    if (isDefined(cloudValue)) {
      mergedApi[field.apiKey] = cloudValue;
      continue;
    }

    mergedApi[field.apiKey] = field.defaultValue;
  }

  return fromApiProfile(mergedApi);
}

export function profileNeedsServerSync(localProfile = {}, cloudProfile = {}) {
  const localApi = toApiProfile(localProfile);
  const cloudApi = normaliseApiProfile(cloudProfile);

  return PROFILE_FIELD_MAP.some(({ apiKey }) => {
    const localValue = localApi[apiKey] ?? null;
    const cloudValue = cloudApi[apiKey] ?? null;
    return JSON.stringify(localValue) !== JSON.stringify(cloudValue);
  });
}

export function profilesAreEquivalent(leftProfile = {}, rightProfile = {}) {
  const leftApi = toApiProfile(leftProfile);
  const rightApi = toApiProfile(rightProfile);

  return PROFILE_FIELD_MAP.every(({ apiKey }) => {
    const leftValue = leftApi[apiKey] ?? null;
    const rightValue = rightApi[apiKey] ?? null;
    return JSON.stringify(leftValue) === JSON.stringify(rightValue);
  });
}
